import { classes, media, stylesheet } from 'typestyle';
import { Header } from '../../layout/header/Header';
import React from 'react';
import { useAppStore } from '../../../store/appStore';
import { MissionsCalendar } from './calendar/MissionsCalendar';
import { BREAKPOINT_LARGE } from '../../layout/styles';
import { MissionsMap } from './map/MissionsMap';
import { MissionNav } from './nav/MissionNav';
import { MissionFilters } from './filters/MissionFilters';
import { MissionsCalendarButton } from './calendar/MissionsCalendarButton';
import { useHasAccessToSchedules } from './useHasAccessToSchedules';
import { FullscreenLoader } from '../../misc/FullscreenLoader';
import { SchedulesTab } from './tabs/SchedulesTab';
import { useSelectedMissionsTab } from './useSelectedMissionsTab';
import { ProjectsTab } from './tabs/ProjectsTab';
import { MissionTab } from '../../../consts/missionTab';
import { MissionWindow } from './openMission/MissionWindow';
import { MissionData } from './types';
import { DateChangeMission } from './dateChangeMission/DateChangeMission';

const css = stylesheet({
  container: {
    maxWidth: 1254,
    width: '100%',
    display: 'flex',
  },
  missionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  navContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    width: 330,
    padding: '24px 12px 12px 0',
    gap: 16,
    marginLeft: 12,
    ...media(
      { maxWidth: BREAKPOINT_LARGE },
      {
        $nest: {
          '&&': {
            margin: 0,
            width: 0,
            padding: 0,
          },
        },
      },
    ),
  },
  navContainerNoScheduleAccess: {
    width: 478,
  },
});

const generateMissionKey = (selectedMission: MissionData) => {
  const { customer, site, schedule, gcpPlacementService } = selectedMission;
  return schedule?.airtableRecordId ?? gcpPlacementService?.airtableRecordId ?? `${customer.id}-${site.id}`;
};

export const MissionsPage = () => {
  const { selectedMission, selectedDateChangeMission, missions } = useAppStore();

  const hasAccessToSchedules = useHasAccessToSchedules();
  const selectedMissionsTab = useSelectedMissionsTab();

  const areSchedulesSelected = selectedMissionsTab === MissionTab.Schedules;
  const areProjectsSelected = selectedMissionsTab === MissionTab.AllProjects;

  const isLoading = hasAccessToSchedules === null || missions === undefined;

  if (isLoading) {
    return <FullscreenLoader />;
  }

  return (
    <div className={css.container}>
      <main className={css.missionsContainer}>
        <Header heading={'My Missions'}>
          {hasAccessToSchedules && (
            <>
              {areSchedulesSelected && <MissionsCalendarButton />}

              <MissionNav />
            </>
          )}

          <MissionFilters />
        </Header>

        {areProjectsSelected ? <ProjectsTab /> : <SchedulesTab key={selectedMissionsTab} />}
      </main>

      <aside className={classes(css.navContainer, !hasAccessToSchedules && css.navContainerNoScheduleAccess)}>
        {areSchedulesSelected && <MissionsCalendar />}

        <MissionsMap key={selectedMissionsTab} />
      </aside>

      {selectedMission && <MissionWindow key={generateMissionKey(selectedMission)} />}

      {selectedDateChangeMission && <DateChangeMission />}
    </div>
  );
};
